import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ArrowLeftOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import {
  setChartFilters as setChartFiltersAction,
  clearChartFilters as clearChartFiltersAction,
  fetchSpecificAssessmentStudentList,
  fetchStudentsByStatus,
  getAssessmentDomains,
  getAssessmentDegrees,
  getAssessmentDepartments,
  getAssessmentSpecializations,
} from '../../actions'
import {
  selectSpecificAssessmentStudentList,
  selectSpecificAssessmentStudentListLoading,
  selectSpecificAssessmentStudentListPagination,
} from '../../selectors'
import CandidateList from './CandidateList'
import StudentReportModal from '../StudentReport'
import AssessmentCharts from '../../components/AssessmentCharts'
import AssessmentNavBar from './components/AssessmentNavBar'

const Wrapper = styled.div`
  width: 100%;
  overflow-y: auto;
`

const HeaderContainer = styled.div`
  width: 100%;
  background-color: #FFFFFF;
  padding: 0 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
`

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  width: 100%;
`

const TopHeader = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 28px;
  color: #3249D7;
`

const BackButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 8px;
  padding: 4px;

  &:hover {
    color: #3249D7;
  }
`

const ContentSection = styled.div`
  padding: 12px 8px;
  width: 100%;
`

const InstituteAssessmentDetails = ({
  assessment,
  onClose,
  instituteId,
  passingYear,
  onSwitchToDiagnosis,
}) => {
  const [studentForReport, setStudentForReport] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10
  const [sortBy, setSortBy] = useState({ field: 'name', order: 'asc' })
  const getInitialNavIndex = () => {
    if (assessment?.isDiagnosis) return 0
    const details = assessment?.assessmentDetails || []
    const normalizedType = (assessment?.assessmentType || '').toLowerCase()
    const hasDiagnosis = ['communication', 'aptitude'].includes(normalizedType)
    if (!hasDiagnosis || !details.length) return 0
    const clickedMapId = assessment?.assessmentInstituteMapId || assessment?.id
    const detailIdx = details.findIndex(d =>
      d.assessmentInstituteMapId === clickedMapId || d.auditId === clickedMapId
    )
    return detailIdx >= 0 ? detailIdx + 1 : 1
  }
  const [currentNavIndex, setCurrentNavIndex] = useState(getInitialNavIndex)

  // Filter state
  const [topLevelDomain, setTopLevelDomain] = useState([])
  const [topLevelDegree, setTopLevelDegree] = useState([])
  const [topLevelDepartment, setTopLevelDepartment] = useState([])
  const [topLevelSpecialization, setTopLevelSpecialization] = useState([])
  const [topLevelPassingYear, setTopLevelPassingYear] = useState(passingYear || null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState([])

  // Local filter options
  const [localDomainOptions, setLocalDomainOptions] = useState([])
  const [localDegreeOptions, setLocalDegreeOptions] = useState([])
  const [localDepartmentOptions, setLocalDepartmentOptions] = useState([])
  const [localSpecializationOptions, setLocalSpecializationOptions] = useState([])

  // Determine if this is a "standard" type (communication/aptitude) or "other" type
  const assessmentTypeLower = (assessment?.assessmentType || '').toLowerCase()
  const isStandardType = ['communication', 'aptitude'].includes(assessmentTypeLower)
  const isOtherType = !isStandardType && !!assessment

  const dispatch = useDispatch()
  const chartFiltersFromRedux = useSelector(state => state.SubscribedInstitutes?.chartFilters)
  const studentList = useSelector(selectSpecificAssessmentStudentList)
  const studentListLoading = useSelector(selectSpecificAssessmentStudentListLoading)
  const studentListPagination = useSelector(selectSpecificAssessmentStudentListPagination)

  // For other types: use Redux studentData from fetchStudentsByStatus
  const otherTypeStudentData = useSelector(state => state.SubscribedInstitutes?.studentData)
  const otherTypeDataLoading = useSelector(state => state.SubscribedInstitutes?.studentDataLoading)
  const otherTypePagination = useSelector(state => state.SubscribedInstitutes?.studentDataPagination)
  // Role-based: sections selected at creation (count > 0), from the same response's assessmentInfo
  const roleBasedSections = useSelector(state => state.SubscribedInstitutes?.assessmentInfo?.roleBasedSections) || []

  const navList = useMemo(() => {
    const details = assessment?.assessmentDetails || []
    if (!details.length) return []
    const normalizedType = (assessment?.assessmentType || '').toLowerCase()
    const hasDiagnosis = ['communication', 'aptitude'].includes(normalizedType)
    if (hasDiagnosis) {
      const diagEntry = {
        isDiagnosis: true,
        title: 'Diagnosis',
        status: assessment.diagnosisStatus || 'Completed',
        startDate: details[0]?.startDate || details[0]?.startTime,
      }
      return [diagEntry, ...details]
    }
    return details
  }, [assessment])

  const activeAssessment = useMemo(() => {
    if (navList.length > 1) {
      return navList[currentNavIndex] || assessment
    }
    return assessment
  }, [navList, currentNavIndex, assessment])

  const isDiagnosisActive = activeAssessment?.isDiagnosis === true

  const selectedAssessmentId = useMemo(() => {
    if (!activeAssessment) return null
    if (activeAssessment.isDiagnosis) {
      return assessment?.scheduleId || null
    }
    return (
      activeAssessment.assessmentInstituteMapID ||
      activeAssessment.assessment_institute_map_id ||
      activeAssessment.assessmentInstituteMapId ||
      activeAssessment.id ||
      activeAssessment.latestAssessmentInstituteMapId ||
      activeAssessment.scheduleId ||
      null
    )
  }, [activeAssessment, assessment])

  const chartAssessment = useMemo(() => {
    if (!activeAssessment || activeAssessment === assessment) return assessment
    return {
      ...assessment,
      ...activeAssessment,
      assessmentType: assessment?.assessmentType,
      scheduleId: assessment?.scheduleId,
      scheduleName: assessment?.scheduleName,
      assessmentDetails: assessment?.assessmentDetails,
    }
  }, [activeAssessment, assessment])

  // Fetch filter options
  useEffect(() => {
    const fetchDomains = async () => {
      // For one-time / standalone assessments, getschedulesInfo sets scheduleId to
      // the assessment_institute_map id (a bogus "schedule" id). Passing it makes the
      // options API take its scheduleId branch and look up a non-existent schedule →
      // empty "No data" dropdowns. Use only the real assessmentId in that case.
      const scheduleId = (isOtherType || assessment?.isOneTime || assessment?.isStandalone) ? '' : (assessment?.scheduleId || '')
      if (!scheduleId && !selectedAssessmentId && !instituteId) return
      const domains = await dispatch(getAssessmentDomains({
        scheduleId,
        assessmentId: selectedAssessmentId || '',
        passingYear: topLevelPassingYear || '',
        instituteId: instituteId || '',
      }))
      if (domains && domains.length > 0) {
        setLocalDomainOptions(domains)
      } else {
        setLocalDomainOptions([])
      }
    }
    fetchDomains()
  }, [assessment?.scheduleId, selectedAssessmentId, topLevelPassingYear, instituteId, dispatch])

  useEffect(() => {
    const fetchDegrees = async () => {
      // For one-time / standalone assessments, getschedulesInfo sets scheduleId to
      // the assessment_institute_map id (a bogus "schedule" id). Passing it makes the
      // options API take its scheduleId branch and look up a non-existent schedule →
      // empty "No data" dropdowns. Use only the real assessmentId in that case.
      const scheduleId = (isOtherType || assessment?.isOneTime || assessment?.isStandalone) ? '' : (assessment?.scheduleId || '')
      if (!scheduleId && !selectedAssessmentId && !instituteId) return
      const degrees = await dispatch(getAssessmentDegrees({
        scheduleId,
        assessmentId: selectedAssessmentId || '',
        passingYear: topLevelPassingYear || '',
        instituteId: instituteId || '',
        domains: [],
      }))
      if (degrees && degrees.length > 0) setLocalDegreeOptions(degrees)
      else setLocalDegreeOptions([])
    }
    fetchDegrees()
  }, [assessment?.scheduleId, selectedAssessmentId, topLevelPassingYear, instituteId, dispatch])

  useEffect(() => {
    const fetchDepartments = async () => {
      // For one-time / standalone assessments, getschedulesInfo sets scheduleId to
      // the assessment_institute_map id (a bogus "schedule" id). Passing it makes the
      // options API take its scheduleId branch and look up a non-existent schedule →
      // empty "No data" dropdowns. Use only the real assessmentId in that case.
      const scheduleId = (isOtherType || assessment?.isOneTime || assessment?.isStandalone) ? '' : (assessment?.scheduleId || '')
      if (topLevelDegree?.length === 0) { setLocalDepartmentOptions([]); return }
      const selectedDegreeValues = topLevelDegree.map(d => d.label || d.value).filter(val => val !== 'all' && val !== 'Select All Degrees')
      if (selectedDegreeValues.length === 0) { setLocalDepartmentOptions([]); return }
      const departments = await dispatch(getAssessmentDepartments({
        scheduleId,
        assessmentId: selectedAssessmentId || '',
        passingYear: topLevelPassingYear || '',
        instituteId: instituteId || '',
        degrees: selectedDegreeValues,
      }))
      setLocalDepartmentOptions(departments && departments.length > 0 ? departments : [])
    }
    fetchDepartments()
  }, [topLevelDegree, assessment?.scheduleId, selectedAssessmentId, topLevelPassingYear, instituteId, dispatch])

  useEffect(() => {
    const fetchSpecializations = async () => {
      // For one-time / standalone assessments, getschedulesInfo sets scheduleId to
      // the assessment_institute_map id (a bogus "schedule" id). Passing it makes the
      // options API take its scheduleId branch and look up a non-existent schedule →
      // empty "No data" dropdowns. Use only the real assessmentId in that case.
      const scheduleId = (isOtherType || assessment?.isOneTime || assessment?.isStandalone) ? '' : (assessment?.scheduleId || '')
      if (topLevelDepartment?.length === 0) { setLocalSpecializationOptions([]); return }
      const selectedDeptValues = topLevelDepartment.map(d => d.label || d.value).filter(val => val !== 'all' && val !== 'Select All Depts.')
      if (selectedDeptValues.length === 0) { setLocalSpecializationOptions([]); return }
      const specializations = await dispatch(getAssessmentSpecializations({
        scheduleId,
        assessmentId: selectedAssessmentId || '',
        passingYear: topLevelPassingYear || '',
        instituteId: instituteId || '',
        departments: selectedDeptValues,
      }))
      setLocalSpecializationOptions(specializations || [])
    }
    fetchSpecializations()
  }, [topLevelDepartment, assessment?.scheduleId, selectedAssessmentId, topLevelPassingYear, instituteId, dispatch])

  // Chart filter functions
  const setChartFilters = (filters) => dispatch(setChartFiltersAction(filters))
  const clearChartFilters = () => dispatch(clearChartFiltersAction())

  const handleBackClick = () => {
    dispatch(clearChartFiltersAction())
    setTopLevelDomain([])
    setTopLevelDegree([])
    setTopLevelDepartment([])
    setTopLevelSpecialization([])
    setTopLevelPassingYear(null)
    setSearchTerm('')
    if (onClose) onClose()
  }

  // Fetch student list
  useEffect(() => {
    if (!selectedAssessmentId) return

    if (isOtherType) {
      // Use fetchStudentsByStatus (calls /assessment/getAssessmentDetails) with
      // backend pagination + the same CandidateList filters scheduled types use.
      dispatch(fetchStudentsByStatus(
        selectedAssessmentId,
        'sent',
        'college',
        searchTerm || '',
        currentPage - 1,
        pageSize,
        {
          degree: topLevelDegree.map(i => i.label || i.value).filter(v => v !== 'all'),
          department: topLevelDepartment.map(i => i.label || i.value).filter(v => v !== 'all'),
          specialization: topLevelSpecialization.map(i => i.label || i.value).filter(v => v !== 'all'),
          status: statusFilter || [],
        },
        true // paginate: this detail renders a single server-paginated list
      ))
      return
    }

    // Standard types: use fetchSpecificAssessmentStudentList
    const apiParams = {
      ...(isDiagnosisActive
        ? { scheduleId: assessment?.scheduleId }
        : { assessmentId: selectedAssessmentId }),
      page: currentPage,
      limit: pageSize,
      search: searchTerm || '',
      passingYear: topLevelPassingYear || '',
      domain: topLevelDomain.map(i => i.label || i.value).filter(v => v !== 'all'),
      degree: topLevelDegree.map(i => i.label || i.value).filter(v => v !== 'all'),
      department: topLevelDepartment.map(i => i.label || i.value).filter(v => v !== 'all'),
      specialization: topLevelSpecialization.map(i => i.label || i.value).filter(v => v !== 'all'),
      Cefr: chartFiltersFromRedux?.cefrLevel ? [chartFiltersFromRedux.cefrLevel] : [],
      AptitudeLevel: chartFiltersFromRedux?.aptitudeLevel ? [chartFiltersFromRedux.aptitudeLevel] : [],
      consistency: chartFiltersFromRedux?.consistency ? [chartFiltersFromRedux.consistency] : [],
      statusFilter: !isDiagnosisActive ? (statusFilter || []) : [],
      sortBy,
    }

    if (instituteId) {
      dispatch(fetchSpecificAssessmentStudentList(instituteId, apiParams))
    }
  }, [
    selectedAssessmentId, isDiagnosisActive, currentPage, searchTerm, topLevelDomain, topLevelDegree,
    topLevelDepartment, topLevelSpecialization, topLevelPassingYear, chartFiltersFromRedux,
    statusFilter, sortBy, instituteId, dispatch, pageSize, isOtherType, assessment?.scheduleId,
  ])

  // Reset status filter when switching to diagnosis or between assessments
  useEffect(() => {
    setStatusFilter([])
  }, [selectedAssessmentId, isDiagnosisActive])

  const handleCandidateClick = (student) => {
    setStudentForReport(student)
  }

  const getAssessmentTypeDisplay = (type) => {
    const typeMap = {
      communication: 'Communication Assessment',
      behavior: 'Behavioral Assessment',
      aptitude: 'Cognitive Aptitude',
      role_based: 'Role Based Assessment',
      custom_assessment: 'Custom Assessment',
      technical_mcq: 'Technical Assessment - MCQ',
      technical_coding: 'Technical Assessment - Coding',
      ai_interview: 'AI Interview',
    }
    return typeMap[type] || type
  }

  const filteredDataForList = useMemo(() => {
    const rawData = studentList || []
    if (!chartFiltersFromRedux || Object.keys(chartFiltersFromRedux).length === 0) return rawData
    const hasCons = !!chartFiltersFromRedux.consistency
    if (!hasCons) return rawData

    return rawData.filter(candidate => {
      if (chartFiltersFromRedux.consistency) {
        const candidateConsistency = candidate.consistencyLevel || candidate.consistency_level || candidate.consistency
        if (!candidateConsistency) return true
        const filterConsistency = chartFiltersFromRedux.consistency.toLowerCase()
        const match = candidateConsistency?.toLowerCase().includes(filterConsistency)
        if (!match) return false
      }
      return true
    })
  }, [studentList, chartFiltersFromRedux])

  if (!assessment) {
    return (
      <Wrapper>
        <HeaderContainer>
          <TopSection>
            <TopHeader>Loading assessment details...</TopHeader>
          </TopSection>
        </HeaderContainer>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <HeaderContainer>
        <TopSection>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BackButton onClick={handleBackClick}>
              <ArrowLeftOutlined style={{ fontSize: 16 }} />
            </BackButton>
            <TopHeader>Assessment Details</TopHeader>
          </div>
        </TopSection>
      </HeaderContainer>

      {/* Schedule Name Header */}
      {assessment && (
        <div style={{
          padding: '12px 16px',
          marginTop: '4px',
          marginBottom: '8px',
          borderBottom: '1px solid #e8e8e8',
        }}>
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
            {(assessment.isOneTime || assessment.isStandalone || !['communication', 'aptitude'].includes((assessment.assessmentType || '').toLowerCase()))
              ? (assessment.scheduleName && assessment.scheduleName !== 'N/A' ? assessment.scheduleName : assessment.name || 'Assessment')
              : isDiagnosisActive
                ? `${assessment.scheduleName}-diagnosis`
                : `${assessment.scheduleName}-schedule-${currentNavIndex || 1}`}
          </span>
          {assessment.assessmentType && (
            <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>
              ({getAssessmentTypeDisplay(assessment.assessmentType)})
            </span>
          )}
        </div>
      )}

      {navList.length > 1 && (
        <AssessmentNavBar
          assessmentList={navList}
          currentIndex={currentNavIndex}
          onNavigate={(idx) => {
            if (idx === 0 && navList[0]?.isDiagnosis && onSwitchToDiagnosis) {
              onSwitchToDiagnosis()
              return
            }
            setCurrentNavIndex(idx)
            setCurrentPage(1)
          }}
        />
      )}

      {/* Assessment Charts - Only for scheduled communication/aptitude (not one-time) */}
      {assessment && isStandardType && !assessment.isOneTime && !assessment.isStandalone && (
        <div style={{ marginBottom: '20px', padding: '0 8px' }}>
          <AssessmentCharts
            assessment={chartAssessment}
            instituteId={instituteId || ''}
            passingYear={topLevelPassingYear || ''}
            studentDataCompleted={[]}
            studentDataSent={[]}
            studentDataPending={[]}
            studentDataInProgress={[]}
            studentDataDroppedOff={[]}
            chartFilters={chartFiltersFromRedux}
            setChartFilters={setChartFilters}
            clearChartFilters={clearChartFilters}
            scheduleId={assessment?.scheduleId || null}
            attemptNumber={activeAssessment?.attemptNumber || null}
          />
        </div>
      )}

      <ContentSection>
        <CandidateList
          data={isOtherType
            ? (otherTypeStudentData?.sent || [])
            : filteredDataForList}
          loading={isOtherType ? otherTypeDataLoading : studentListLoading}
          totalCount={isOtherType
            ? (otherTypePagination?.totalCount || otherTypeStudentData?.sent?.length || 0)
            : (studentListPagination?.total || 0)}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onCandidateClick={handleCandidateClick}
          assessmentType={assessment?.assessmentType}
          assessmentId={selectedAssessmentId}
          enabledSections={assessment?.enabledSections}
          roleBasedSections={roleBasedSections}
          chartFilters={chartFiltersFromRedux}
          topLevelDomain={topLevelDomain}
          setTopLevelDomain={setTopLevelDomain}
          topLevelDegree={topLevelDegree}
          setTopLevelDegree={setTopLevelDegree}
          topLevelDepartment={topLevelDepartment}
          setTopLevelDepartment={setTopLevelDepartment}
          topLevelSpecialization={topLevelSpecialization}
          setTopLevelSpecialization={setTopLevelSpecialization}
          topLevelPassingYear={topLevelPassingYear}
          setTopLevelPassingYear={setTopLevelPassingYear}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          domainOptionsFromProps={localDomainOptions}
          degreeOptionsFromProps={localDegreeOptions}
          departmentOptionsFromProps={localDepartmentOptions}
          specializationOptionsFromProps={localSpecializationOptions}
          instituteId={instituteId}
          onSortChange={setSortBy}
          scheduleName={assessment?.scheduleName}
          scheduleNumber={assessment?.scheduleNumber}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          showStatusFilter={!isDiagnosisActive}
        />
      </ContentSection>

      {/* Student Report Modal - admin-react's version */}
      <StudentReportModal
        visible={!!studentForReport}
        onClose={() => setStudentForReport(null)}
        student={studentForReport}
        allowProctoring={true}
        enabledSections={assessment?.enabledSections}
        assessmentType={assessment?.assessmentType}
      />
    </Wrapper>
  )
}

export default InstituteAssessmentDetails
